import React, { useState, useEffect } from 'react';
import { Video, Save, ExternalLink, Play, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { movieApi } from '../../services/api';

const AdminTrailer = () => {
  const [trailerUrl, setTrailerUrl] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchMovie = async () => {
      try {
        const res = await movieApi.get();
        setTrailerUrl(res.data.trailerUrl || '');
        setPreviewUrl(res.data.trailerUrl || '');
      } catch (error) {
        console.error('Error fetching movie:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMovie();
  }, []);

  const handlePreview = () => {
    setPreviewUrl(trailerUrl);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await movieApi.update({ trailerUrl });
      alert('Trailer guardado correctamente');
    } catch (error) {
      console.error('Error saving trailer:', error);
      alert('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const getEmbedUrl = (url) => {
    if (!url) return '';
    if (url.includes('/embed/')) return url;
    const videoIdMatch = url.match(/(?:watch\?v=|youtu\.be\/)([^&]+)/);
    if (videoIdMatch) {
      return `https://www.youtube.com/embed/${videoIdMatch[1]}`;
    }
    return url;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Trailer</h1>
          <p className="text-gray-500">Configura el trailer de la película</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#e84c8a] hover:bg-[#d43d7a]">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Guardar Cambios
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Video className="w-5 h-5 text-[#e84c8a]" />
              Configuración del Trailer
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="trailerUrl">URL del Trailer (YouTube)</Label>
              <Input
                id="trailerUrl"
                value={trailerUrl}
                onChange={(e) => setTrailerUrl(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=... o https://www.youtube.com/embed/..."
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Acepta URLs de YouTube en formato watch o embed
              </p>
            </div>

            <div className="flex gap-2">
              <Button onClick={handlePreview} variant="outline" className="flex-1">
                <Play className="w-4 h-4 mr-2" />
                Vista Previa
              </Button>
              <Button 
                variant="outline"
                onClick={() => window.open(trailerUrl, '_blank')}
              >
                <ExternalLink className="w-4 h-4" />
              </Button>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Formatos soportados:</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• https://www.youtube.com/watch?v=VIDEO_ID</li>
                <li>• https://www.youtube.com/embed/VIDEO_ID</li>
                <li>• https://youtu.be/VIDEO_ID</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Vista Previa</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="aspect-video bg-black rounded-lg overflow-hidden">
              {previewUrl ? (
                <iframe
                  src={getEmbedUrl(previewUrl)}
                  title="Trailer Preview"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/50">
                  <p>Introduce una URL para ver la vista previa</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminTrailer;
